/**
 * « Arène » d'un live (design Live School, migration 0658) : tout l'écran d'un
 * défi (salon, jeu, résultats, page d'invitation) est SOMBRE, quel que soit le
 * mode choisi dans le profil — la cover du défi en fond, sous un voile bleu
 * nuit. `.theme-ocean` posé sur le conteneur bascule tous les tokens (cartes en
 * verre, textes clairs) ; une carte claire se pose avec `.theme-paper`.
 */

import type { ReactNode } from 'react';
import { ArrowLeft, type LucideIcon } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

/** Intensité du voile : `hero` laisse voir le haut de la photo (jeu). */
export type ArenaTone = 'hero' | 'medium' | 'strong';

const VEIL: Record<ArenaTone, string> = {
  hero: 'bg-[linear-gradient(180deg,rgba(6,20,51,0.30)_0%,rgba(6,20,51,0.72)_32%,rgba(6,20,51,0.96)_62%,#061433_100%)]',
  medium:
    'bg-[linear-gradient(180deg,rgba(6,20,51,0.42)_0%,rgba(6,20,51,0.78)_45%,rgba(6,20,51,0.96)_100%)]',
  strong:
    'bg-[linear-gradient(180deg,rgba(6,20,51,0.55)_0%,rgba(6,20,51,0.88)_40%,rgba(6,20,51,0.98)_100%)]',
};

export function LiveArena({
  coverUrl,
  tone = 'medium',
  className,
  children,
}: {
  coverUrl: string | null;
  tone?: ArenaTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      data-testid="live-arena"
      className={cn('theme-ocean text-ink-900 relative isolate min-h-dvh bg-[#061433]', className)}
    >
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 bg-[#061433]">
        {coverUrl && (
          <img src={coverUrl} alt="" decoding="async" className="h-full w-full object-cover" />
        )}
        <div className={cn('absolute inset-0', VEIL[tone])} />
        <div className="absolute inset-0 bg-[radial-gradient(120%_55%_at_50%_-12%,rgba(15,49,117,0.55)_0%,rgba(15,49,117,0)_62%)]" />
      </div>
      {children}
    </div>
  );
}

/** Bouton rond de verre (retour, partage…), 44 px. */
export function ArenaIconButton({
  label,
  onClick,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[rgba(255,255,255,0.1)] text-white ring-1 ring-[rgba(255,255,255,0.2)] backdrop-blur transition active:scale-[0.95]',
        className,
      )}
    >
      {children}
    </button>
  );
}

/**
 * En-tête d'un écran de l'arène : boutons ronds sur une ligne (retour à
 * gauche), puis le titre centré DESSOUS (jamais sur la même ligne que le
 * retour, cf. règles mobile-first).
 */
export function ArenaHeader({
  onBack,
  eyebrow,
  title,
  subtitle,
  right,
}: {
  onBack: () => void;
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <header className="mb-5">
      <div className="flex items-center justify-between gap-2">
        <ArenaIconButton label="Retour" onClick={onBack}>
          <ArrowLeft className="h-5 w-5" aria-hidden />
        </ArenaIconButton>
        {right}
      </div>
      <div className="mt-2 text-center">
        {eyebrow && (
          <p className="font-display text-xs font-bold uppercase tracking-[0.18em] text-[#f4c64e]">
            {eyebrow}
          </p>
        )}
        <h1 className="font-display mt-1 text-[1.65rem] font-extrabold leading-tight text-white">
          {title}
        </h1>
        {subtitle && <p className="text-ink-600 mt-1 text-sm">{subtitle}</p>}
      </div>
    </header>
  );
}

/** Carte de verre de l'arène. */
export function ArenaPanel({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'rounded-3xl bg-[rgba(12,34,78,0.55)] p-4 shadow-[0_10px_30px_rgba(0,6,24,0.35)] ring-1 ring-[rgba(255,255,255,0.1)] backdrop-blur-md',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Liste de verre « ce qui t'attend » (icône dans une pastille bleue + texte). */
export function ArenaInfoList({
  items,
  className,
}: {
  items: { icon: LucideIcon; text: ReactNode }[];
  className?: string;
}) {
  if (items.length === 0) return null;
  return (
    <ArenaPanel className={cn('p-2', className)}>
      <ul className="divide-y divide-[rgba(255,255,255,0.1)]">
        {items.map((item, i) => {
          const Icon = item.icon;
          return (
            <li key={i} className="flex items-center gap-3 px-2 py-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(5,102,242,0.25)] text-[#9cc4f8]">
                <Icon className="h-[18px] w-[18px]" aria-hidden />
              </span>
              <span className="text-ink-800 text-sm leading-snug">{item.text}</span>
            </li>
          );
        })}
      </ul>
    </ArenaPanel>
  );
}
